import { nicheLabel } from '@/components/companion/preference-labels';
import { MatchFlames } from '@/components/recommendations/MatchFlames';
import { Box, Flex, Image, Text } from '@chakra-ui/react';
import type { MatchTier, RecommendationDto } from '@fluenceur/types';
import { Lock } from 'iconoir-react';
import { useTranslation } from 'react-i18next';

/** Stand-in for the handle: the free plan masks handles, so the real one never renders. */
const MASKED_HANDLE = '@••••••••••';

const compactFollowers = (count: number | null | undefined, locale: string): string | null => {
  if (!count) return null;
  try {
    return new Intl.NumberFormat(locale, { notation: 'compact', maximumFractionDigits: 1 }).format(
      count
    );
  } catch {
    return String(count);
  }
};

/**
 * One example creator floating on the panel (mock-up v3): avatar, name, a
 * masked handle, the app's flame scale and a meta line. The mock-up's drop
 * shadow is a border here, as everywhere in the app. Discovery's empty state
 * draws the same illustrative creators with their handle in plain sight and,
 * in UGC mode, its own meta line.
 */
export const CompanionExampleCard = ({
  recommendation,
  niche,
  rotation,
  maskHandle = true,
  meta: metaOverride,
}: {
  recommendation: RecommendationDto;
  niche: string | null;
  rotation: number;
  /** Blurs the handle as the free plan does; false shows the example's own. */
  maskHandle?: boolean;
  /** Replaces the niche and followers line. */
  meta?: string;
}) => {
  const { t } = useTranslation();
  const profile = recommendation.creatorProfile;
  const followers = compactFollowers(profile?.followersCount, t('common.locale'));
  const meta =
    metaOverride ?? [niche ? nicheLabel(niche, t) : null, followers].filter(Boolean).join(' · ');

  return (
    <Box
      data-testid="companion-example-creator"
      w="262px"
      maxW="full"
      p="15px"
      bg="white"
      borderRadius="18px"
      borderWidth="1px"
      borderColor="cardBorder"
      transform={`rotate(${rotation}deg)`}
    >
      <Flex align="center" gap="12px">
        <Image
          src={profile?.profilePictureUrl ?? undefined}
          alt=""
          w="44px"
          h="44px"
          borderRadius="full"
          objectFit="cover"
          flex="none"
        />
        <Box minW={0} flex="1">
          <Text fontFamily="heading" fontSize="14.5px" fontWeight="600" color="ink" truncate>
            {profile?.displayName}
          </Text>
          {maskHandle ? (
            <Flex align="center" gap="5px" color="inkSubtle">
              <Text as="span" fontSize="12.5px" filter="blur(4px)" userSelect="none" aria-hidden>
                {MASKED_HANDLE}
              </Text>
              <Box as="span" role="img" aria-label={t('onboarding.brandPanel.handleLocked')}>
                <Lock width={12} height={12} aria-hidden />
              </Box>
            </Flex>
          ) : (
            <Text fontSize="12.5px" color="inkSubtle" truncate>
              @{profile?.username}
            </Text>
          )}
        </Box>
      </Flex>
      <Flex align="center" justify="space-between" gap="8px" mt="12px">
        <MatchFlames tier={recommendation.matchTier as MatchTier} size="xs" />
        {meta && (
          <Text fontSize="12px" color="inkMuted" truncate>
            {meta}
          </Text>
        )}
      </Flex>
    </Box>
  );
};
